import { ApiError, NetworkError } from "@sos/api-client";

import i18n, { type Namespace } from "../../app/i18n";

/** Interpolation values from the error envelope; `retry_after_s` (BE-20) also becomes `minutes`. */
export function errorParams(error: unknown): Record<string, unknown> {
  if (!(error instanceof ApiError)) return {};
  const params: Record<string, unknown> = { ...error.params };
  const seconds = Number(params.retry_after_s ?? params.retry_after_seconds);
  params.minutes = Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds / 60) : 1;
  return params;
}

/** Untyped lookup for keys built from API codes (`errors.<code>`); typed `t` cannot know them. */
export function translateKey(key: string, values?: Record<string, unknown>): string {
  return i18n.t(key as never, values as never) as unknown as string;
}

/** True only for keys that hold a text (`errors.internal` is a group since `internal.error`, BE-20). */
export function hasKey(key: string): boolean {
  return (
    i18n.exists(key) && typeof i18n.t(key as never, { returnObjects: true } as never) === "string"
  );
}

/**
 * FE-03 FR-FE03-27: `<area>:errors.<code>` → `common:errors.<code>` → server `message` →
 * `common:errors.generic`.
 */
export function translateError(error: unknown, area?: Namespace): string {
  const code = error instanceof ApiError || error instanceof NetworkError ? error.code : "unknown";
  const params = errorParams(error);
  const keys = [
    area && area !== "common" ? `${area}:errors.${code}` : null,
    `common:errors.${code}`,
  ];
  for (const key of keys) {
    if (key && hasKey(key)) return translateKey(key, params);
  }
  if (error instanceof ApiError && error.message) return error.message;
  return translateKey("common:errors.generic");
}

/**
 * Field names of a `validation.failed` error (SH-02 FR-SH02-33: `params.fields` is
 * `[{field, reason}]`; plain strings are accepted too).
 */
export function errorFields(error: unknown): string[] {
  if (!(error instanceof ApiError)) return [];
  const fields = error.params.fields;
  if (!Array.isArray(fields)) return [];
  return fields
    .map((f: unknown) =>
      typeof f === "string"
        ? f
        : typeof f === "object" && f !== null && "field" in f
          ? String((f as { field: unknown }).field)
          : "",
    )
    .filter(Boolean);
}

export function isApiErrorCode(error: unknown, ...codes: string[]): error is ApiError {
  return error instanceof ApiError && codes.includes(error.code);
}

export function isNetworkError(error: unknown): error is NetworkError {
  return error instanceof NetworkError;
}
